import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Logo } from './Logo';

// Kanäle wurde in Settings integriert
const nav = [
  { to: '/',         label: 'Dashboard',  icon: '▦' },
  { to: '/generate', label: 'Generieren', icon: '✦' },
  { to: '/history',  label: 'Meine Posts', icon: '◷' },
  { to: '/video',    label: 'Video',      icon: '▶' },
  { to: '/settings', label: 'Settings',   icon: '⚙' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  return (
    <div style={{ display: 'flex', minHeight: '100dvh' }}>

      {/* ── Sidebar ── */}
      <aside className="sidebar-desktop" style={{
        width: 240, flexShrink: 0, position: 'sticky', top: 0, height: '100dvh',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
        background: isDark ? 'rgba(5,5,15,0.95)' : 'rgba(248,252,255,0.96)',
        borderRight: `1px solid var(--glass-border)`,
        backdropFilter: 'blur(24px)'
      }}>
        {isDark && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 160, background: 'radial-gradient(ellipse at 50% 0%, rgba(0,100,220,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />}

        {/* Logo */}
        <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid var(--glass-border)`, position: 'relative' }}>
          <Logo size="md" fill />
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8 }}>
            <span className="dot-live" style={{ width: 5, height: 5 }} />
            <span style={{ fontSize: 9, color: 'var(--green)', fontFamily: 'Outfit', letterSpacing: '0.1em' }}>SYSTEM ONLINE</span>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 10px 4px' }}>
          {nav.map(({ to, label, icon }) => (
            <NavLink key={to} to={to} end={to === '/'}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10, marginBottom: 2,
                fontSize: 14, fontFamily: 'Outfit', fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--cyan)' : 'var(--text2)',
                background: isActive ? 'linear-gradient(90deg, var(--cyan-dim), transparent)' : 'transparent',
                borderLeft: `2px solid ${isActive ? 'var(--cyan)' : 'transparent'}`,
                transition: 'all 0.15s',
                textShadow: isActive && isDark ? '0 0 12px rgba(0,255,255,0.3)' : 'none'
              })}
            >
              {({ isActive }) => (
                <>
                  <span style={{
                    fontSize: 17, opacity: isActive ? 1 : 0.5, flexShrink: 0,
                    color: isActive ? 'var(--cyan)' : 'inherit',
                    filter: isActive && isDark ? 'drop-shadow(0 0 4px rgba(0,255,255,0.5))' : 'none'
                  }}>{icon}</span>
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Divider */}
        <div style={{ margin: '4px 16px 8px', height: 1, background: 'linear-gradient(90deg, transparent, var(--glass-border), transparent)' }} />

        {/* Theme Toggle */}
        <div style={{ padding: '0 12px 8px' }}>
          <button onClick={toggleTheme} style={{
            width: '100%', padding: '8px 12px',
            background: 'var(--glass)', border: '1px solid var(--glass-border)',
            borderRadius: 10, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            transition: 'all 0.15s', fontFamily: 'Outfit', color: 'var(--text2)', fontSize: 13
          }}>
            <span>{isDark ? '☀ Light Mode' : '🌙 Dark Mode'}</span>
            <div style={{ width: 34, height: 18, borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, var(--cyan), var(--blue))', border: '1px solid var(--glass-border)', position: 'relative', transition: 'all 0.2s' }}>
              <div style={{ position: 'absolute', top: 2, left: isDark ? 2 : 16, width: 12, height: 12, borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.4)' : '#fff', transition: 'left 0.2s' }} />
            </div>
          </button>
        </div>

        {/* User */}
        <div style={{ padding: '0 12px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '8px 10px', background: 'var(--glass)', borderRadius: 10, border: '1px solid var(--glass-border)' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, var(--cyan), var(--blue))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: isDark ? '#000' : '#fff' }}>
              {(user?.name || user?.email || '?')[0].toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'Outfit' }} className="truncate">{user?.name || 'Nutzer'}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }} className="truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} className="btn-ghost" style={{ width: '100%', fontSize: 12, padding: '6px 0', justifyContent: 'center' }}>
            Abmelden
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main style={{ flex: 1, minWidth: 0, overflowX: 'hidden' }}>
        <Outlet />
      </main>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="nav-mobile" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
        background: isDark ? 'rgba(5,5,15,0.97)' : 'rgba(248,252,255,0.97)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid var(--glass-border)', display: 'flex',
        padding: '6px 0 calc(6px + env(safe-area-inset-bottom))'
      }}>
        {nav.map(({ to, label, icon }) => (
          <NavLink key={to} to={to} end={to === '/'}
            style={({ isActive }) => ({
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              fontSize: 9, fontFamily: 'Outfit', fontWeight: 500,
              color: isActive ? 'var(--cyan)' : 'var(--muted)', transition: 'color 0.15s'
            })}
          >
            {({ isActive }) => (
              <>
                <span style={{ width: 32, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isActive ? 'var(--cyan-dim)' : 'transparent', borderRadius: 7, fontSize: 16, filter: isActive && isDark ? 'drop-shadow(0 0 4px rgba(0,255,255,0.5))' : 'none' }}>{icon}</span>
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <style>{`
        @media (min-width: 768px) { .nav-mobile { display: none !important; } }
        @media (max-width: 767px) { .sidebar-desktop { display: none !important; } main { padding-bottom: 72px; } }
      `}</style>
    </div>
  );
}
